import { css } from "@emotion/react";
import clsx from "clsx";
import { Fragment, createContext, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import Icon from "./Icon.jsx";
import useThemeProps from "./useThemeProps.jsx";
import useUrlTransformer from "./useUrlTransformer.js";

const clickableContext = createContext(null);

function getUrlType(url) {
  if (url.startsWith("#")) {
    return "anchor";
  }
  try {
    let { protocol, hostname } = new URL(url);
    if (protocol && protocol !== "http:" && protocol !== "https:") {
      return protocol.substring(0, protocol.length - 1);
    }
    if (hostname) {
      return "external";
    }
  } catch {
    // Continue
  }
  return "internal";
}

export default function useClickable(inProps) {
  const { t } = useTranslation();
  inProps = useThemeProps({ props: inProps, name: "Clickable" });
  let {
    // as,
    elements = {},
    href,
    htmlFor,
    onClick,
    rel,
    target,
    to,
    type,
    uri,
    url,
    className,
    children,
    title,
    label,
    append,
    prepend,
    hideExternalIcon = false,
    externalIcon,
    ...restProps
  } = inProps;

  if (!children) {
    children = title ?? label;
    title = undefined;
    label = undefined;
  }

  const parentClickableContext = useContext(clickableContext);

  href =
    href != null && href !== ""
      ? href
      : to != null && to !== ""
      ? to
      : url != null && url !== ""
      ? url
      : uri != null && uri !== ""
      ? uri
      : null;

  const { transformUrl } = useUrlTransformer();

  href = transformUrl(href);

  /**
   * Add rel="noopener noreferrer" to links with target="_blank"
   */
  if (target === "_blank") {
    rel = [
      ...new Set([...(rel ? rel.split(/\s+/g) : []), "noopener", "noreferrer"]),
    ].join(" ");
  }

  let opensNewWindow = target === "_blank";

  children = (
    <Fragment>
      {prepend}
      {children}
      {opensNewWindow && !hideExternalIcon && (
        <Icon
          name={externalIcon}
          aria-label={t([
            "wsui.base.clickable.opensNewWindow",
            "Opens new window",
          ])}
          css={css`
            padding: 0 0.25em;
          `}
        />
      )}
      {append}
    </Fragment>
  );

  type =
    parentClickableContext && parentClickableContext.type !== "inert"
      ? "inert"
      : type ||
        (htmlFor
          ? "label"
          : opensNewWindow
          ? "external"
          : href != null
          ? getUrlType(href)
          : onClick
          ? "button"
          : "inert");

  let isDisabled = inProps.disabled || inProps["aria-disabled"] === "true";

  let isInteractive = type !== "inert" && !isDisabled;

  let Element =
    // as ||
    type === "inert"
      ? elements.inert || "span"
      : type === "label"
      ? elements.label || "label"
      : type === "submit"
      ? elements.submit || elements.button || "button"
      : type === "reset"
      ? elements.reset || elements.button || "button"
      : type === "button"
      ? elements.button || "button"
      : elements[type] || elements.link || "a";

  const ElementWithProvider = useMemo(() => {
    if (parentClickableContext) {
      return Element;
    }
    const ElementWithContextProvider = ({ children, ...props }) => (
      <Element {...props}>
        <clickableContext.Provider value={{ type }}>
          {children}
        </clickableContext.Provider>
      </Element>
    );
    return ElementWithContextProvider;
  }, [!!parentClickableContext, type, Element]);

  className = clsx(className, {
    "wsui-is-interactive": isInteractive,
    "wsui-is-disabled": isDisabled,
    "wsui-opens-new-window": opensNewWindow,
  });

  return {
    Element: ElementWithProvider,
    type,
    isInteractive,
    isDisabled,
    opensNewWindow,
    props: {
      className,
      href,
      onClick,
      htmlFor,
      target,
      rel,
      type:
        type === "button" || type === "submit" || type === "reset"
          ? type
          : undefined,
      children,
      title,
      ...restProps,
    },
  };
}
